import { ClassNamesFromIStyles, Dropdown, Pivot, useClassNames } from '@h2oai/ui-kit';
import { ChartData } from 'chart.js';
import { useState } from 'react';

import { useTimeFilters } from './common/hooks';
import { eScorerErrorColumns, eScorerLatencyColumns } from './components/EScorer/EScorer.columns';
import { useEScorerErrorEvents, useEScorerLatencyEvents } from './components/EScorer/EScorer.hooks';
import {
  dropdownStyles,
  escorerPageStylesDefault,
  IEScorerPageStyles,
  pivotStyles,
} from './components/EScorer/EScorer.styles';
import {
  EScorerErrorColumns,
  EScorerLatencyColumns,
  eScorerScoreModeOptions,
} from './components/EScorer/EScorer.utils';
import EScorerDataDisplay from './components/EScorer/EScorerDataDisplay/EScorerDataDisplay';
import MultiSelectDialog from './components/MultiSelect/MultiSelect';
import TimeFilters from './components/TimeFilters/TimeFilters';
import PageWrapper from './PageWrapper';

export default function EScorer() {
  const [selectedModels, setSelectedModels] = useState<string[] | undefined>(undefined);
  const [selectedScoreModes, setSelectedScoreModes] = useState<string[]>(['REAL_TIME', 'BATCH']);

  const handleSelectionChange = (selected: string[]) => {
    setSelectedModels(selected);
  };

  const { startDate, endDate, selectedTimeFrame, handleTimeFilterChange } = useTimeFilters();

  const eScorerLatencyData = useEScorerLatencyEvents(startDate, endDate, selectedModels, selectedScoreModes);
  const eScorerErrorData = useEScorerErrorEvents(startDate, endDate, selectedModels, selectedScoreModes);

  const classNames = useClassNames<IEScorerPageStyles, ClassNamesFromIStyles<IEScorerPageStyles>>(
    'escorer-page',
    escorerPageStylesDefault
  );

  const fallbackChartData: ChartData<'line'> = {
    labels: [],
    datasets: [],
  };

  return (
    <PageWrapper>
      <div className={classNames.filterContainer}>
        <div className={classNames.dropdowns}>
          <MultiSelectDialog
            options={eScorerLatencyData.availableModels.map((model) => ({ key: model, text: model })) || []}
            onChange={handleSelectionChange}
            label="Select Model(s)"
            placeholder="Select Model(s)"
            searchPlaceholder="Search Models"
            initialSelectedOptionKeys={
              selectedModels === undefined ? eScorerLatencyData.availableModels : selectedModels
            }
          />
          <Dropdown
            multiSelect
            errorMessage=""
            label="Select Score Mode"
            loadingMessage=""
            selectedKeys={selectedScoreModes}
            onChange={(_, option) => {
              if (!option) return;
              setSelectedScoreModes((prevSelectedKeys) => {
                if (option.selected) {
                  return [...(prevSelectedKeys || []), option.key as string];
                } else {
                  return (prevSelectedKeys || []).filter((key) => key !== option.key);
                }
              });
            }}
            options={eScorerScoreModeOptions}
            placeholder="Select Score Mode"
            styles={dropdownStyles}
          />
        </div>
        <TimeFilters
          startDate={startDate}
          endDate={endDate}
          selectedTimeFrame={selectedTimeFrame}
          onChange={handleTimeFilterChange}
        />
      </div>
      <Pivot
        items={[
          {
            content: (
              <EScorerDataDisplay
                chartData={(eScorerLatencyData.latencyChartData || fallbackChartData) as ChartData<'line'>}
                chartTitle={''}
                tableData={eScorerLatencyData.eScorerEvents || []}
                tableColumns={eScorerLatencyColumns}
                loading={eScorerLatencyData.loading}
                error={!!eScorerLatencyData.error}
                sortableFields={[
                  EScorerLatencyColumns.Date,
                  EScorerLatencyColumns.AvgLatency,
                  EScorerLatencyColumns.DataPointsCount,
                ]}
                groupableFields={[
                  EScorerLatencyColumns.Date,
                  EScorerLatencyColumns.ModelName,
                  EScorerLatencyColumns.ScoreMode,
                ]}
              />
            ),
            headerText: 'Latency',
          },
          {
            content: (
              <EScorerDataDisplay
                chartData={(eScorerErrorData.eScorerErrorChartData || fallbackChartData) as ChartData<'line'>}
                chartTitle={''}
                tableData={eScorerErrorData.eScorerErrorEvents || []}
                tableColumns={eScorerErrorColumns}
                loading={eScorerErrorData.loading}
                error={!!eScorerErrorData.error}
                sortableFields={[EScorerErrorColumns.Date, EScorerErrorColumns.TotalErrors]}
                groupableFields={[
                  EScorerErrorColumns.Date,
                  EScorerErrorColumns.ModelName,
                  EScorerErrorColumns.ScoreMode,
                ]}
              />
            ),
            headerText: 'Errors',
          },
        ]}
        styles={pivotStyles}
      />
    </PageWrapper>
  );
}
